import { useEffect, useState } from "react";
import { Box, Chip, Tooltip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  clearApiBaseOverride,
  getApiBase,
  getApiBaseOverride,
} from "../utils/apiBase";

/**
 * Floating badge showing which backend the FE is currently calling.
 *
 * Renders when:
 *   - Vite dev server is running (any localhost dev), OR
 *   - The user has an explicit runtime override (`?backend=pr-N`), OR
 *   - The FE is deployed under a per-PR hostname (pr-N.mitigate.uat.fi-sci.ai)
 *
 * Hidden on UAT and prod default deploys so production users never see it.
 */
export default function BackendBadge() {
  // Re-render when the override changes (e.g. someone navigates with
  // ?backend=...). Listening to popstate covers SPA navigations; storage
  // event covers other tabs.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("popstate", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("popstate", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const override = getApiBaseOverride();
  const isPrFe = /^pr-\d+\./.test(window.location.hostname);
  const isDev = import.meta.env.DEV;

  // Hide on UAT and prod default deploys. In dev mode (Vite serving on
  // localhost) and PR review envs, show by default.
  if (!override && !isPrFe && !isDev) return null;

  // In dev mode, getApiBase() returns http://localhost:5173 (so requests
  // hit the Vite proxy) — not useful to display. Surface the proxy target
  // instead, which is the *real* backend being talked to.
  const proxyTarget = (import.meta.env.VITE_API_PROXY_TARGET as string | undefined) ?? "";
  const displayUrl = (isDev && proxyTarget && !override) ? proxyTarget : getApiBase();

  let hostname: string;
  try {
    hostname = new URL(displayUrl).hostname;
  } catch {
    hostname = displayUrl;
  }
  const prMatch = hostname.match(/^pr-(\d+)-/);
  let label: string;
  if (prMatch) label = `BE: pr-${prMatch[1]}`;
  else if (isDev && !proxyTarget && !override) label = "BE: (dev, no proxy)";
  else label = `BE: ${hostname}`;
  const color = override ? "warning" : "info";

  const onCopy = () => {
    navigator.clipboard.writeText(displayUrl).catch(() => {});
  };
  const onClear = () => {
    clearApiBaseOverride();
    // Hard reload so all in-flight cached state resets cleanly.
    window.location.reload();
  };

  return (
    <Box
      // bottom-right pill, above MUI snackbars (z=1400) but below modals
      sx={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 1300,
        pointerEvents: "auto",
      }}
      // Re-render hook: read tick into a sx noop so changes flush.
      data-tick={tick}
    >
      <Tooltip
        title={
          <>
            {displayUrl}
            <br />
            {override
              ? "Override active — click ✕ to reset to default."
              : isDev
              ? "Vite dev server (proxying API calls)."
              : "Per-PR review environment."}
          </>
        }
        arrow
      >
        <Chip
          color={color}
          size="small"
          label={label}
          icon={
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              sx={{ color: "inherit", p: 0.25, ml: 0.5 }}
              aria-label="Copy backend URL"
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          }
          onDelete={override ? onClear : undefined}
          deleteIcon={
            override ? (
              <CloseIcon aria-label="Clear backend override" />
            ) : undefined
          }
          sx={{
            fontWeight: 600,
            fontFamily: "monospace",
            opacity: 0.92,
            boxShadow: 2,
            "&:hover": { opacity: 1 },
          }}
        />
      </Tooltip>
    </Box>
  );
}
